import { putApiChatEvaluationTestSetRename, useGetApiChatEvaluationTestSetList, useGetApiWorkspaceGet, usePostApiChatEvaluationTestSetCreate, usePutApiChatEvaluationTestSetDelete, usePutApiChatEvaluationTestSetRename } from '@/API';
import { ActionIcon, Box, Button, Group, Text, Pagination, Stack, LoadingOverlay } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { AppBreadcrumbs } from '@/Components/AppBreadcrumbs';

export function ChatEvaluationTestSetList() {
    const { workspaceId } = useParams({ from: "/workspace/$workspaceId/chatEvaluationTestSet/" })
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })

    const PAGE_SIZE = 10
    const [activePage, setPage] = useState(1);
    const list = useGetApiChatEvaluationTestSetList({ workspaceId: workspaceId, skip: (activePage - 1) * PAGE_SIZE, limit: PAGE_SIZE })

    const rename_Mutation = usePutApiChatEvaluationTestSetRename()
    const delete_Mutation = usePutApiChatEvaluationTestSetDelete()
    const create_Mutation = usePostApiChatEvaluationTestSetCreate()
    const [createLoading, setCreateLoading] = useState(false)

    return <Stack pos="relative" gap={5}>
        <LoadingOverlay visible={list.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '聊天評估',
                    active: true,
                }
            ]}
        />
        <Group>
            <Button loading={createLoading} onClick={() => {
                modals.openContextModal({
                    modal: "TextInput",
                    title: "創建測試集",
                    innerProps: {
                        textInputProps: {
                            label: "測試集名稱"
                        },
                        onConfirm: async v => {
                            setCreateLoading(true)
                            try {
                                await create_Mutation.mutateAsync({
                                    params: {
                                        workspaceId: workspaceId, name: v
                                    }
                                })
                                queryClient.invalidateQueries({ queryKey: list.queryKey })
                                notifications.show({ message: "創建成功" })

                            } catch (e) {
                                notifications.show({ message: "創建失敗", color: "red" })

                            }
                            setCreateLoading(false)
                        }
                    }
                })
            }}>創建測試集</Button>
        </Group>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil((list.data?.data.total ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={(list.data?.data.total ?? 0) > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            columns={[
                { accessor: 'id', title: "Id" },
                { accessor: 'name', title: "名稱" },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "TextInput",
                                        title: '修改名稱',
                                        innerProps: {
                                            initText: data.name ?? "",
                                            onConfirm: async (v) => {
                                                try {
                                                    if (data.id) {
                                                        await rename_Mutation.mutateAsync(
                                                            {
                                                                params: {
                                                                    workspaceId: workspaceId,
                                                                    id: data.id,
                                                                    name: v
                                                                }
                                                            })
                                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                        notifications.show({ message: "修改成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "修改失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "ConfirmCountDown",
                                        title: '刪除',
                                        innerProps: {
                                            countDownSecond: 1,
                                            children: (
                                                <Text size="sm">
                                                    是否要刪除"{data.name}"?
                                                </Text>
                                            ),
                                            labels: {
                                                confirm: '刪除', cancel: '取消'
                                            },
                                            confirmProps: { color: 'red' },
                                            onConfirm: async () => {
                                                try {
                                                    if (data.id) {
                                                        await delete_Mutation.mutateAsync({
                                                            params: {
                                                                workspaceId: workspaceId,
                                                                id: data.id
                                                            }
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                        notifications.show({ message: "刪除成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "刪除失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ),
                },
            ]}
            onRowDoubleClick={({ record }) => {
                navigate({
                    to: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId", params: {
                        workspaceId: workspaceId!,
                        chatEvaluationTestSetId: record.id!
                    }
                })
            }}
            records={list.data?.data.results ?? []}
        />
    </Stack>
}